/**
 * R4 — hand-written narrowing helpers, not Zod: zero dependencies, and they port to Kotlin
 * line-for-line. `JSON.parse` stays outside core; everything in here starts from `unknown`.
 */
export type RawObject = Readonly<Record<string, unknown>>;

export function asObject(value: unknown): RawObject | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return value as RawObject;
}

export function asArray(value: unknown): readonly unknown[] | null {
  return Array.isArray(value) ? (value as readonly unknown[]) : null;
}

export function asString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

export function asNonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

export function asBoolean(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

export function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Accepts a number *or* a numeric string. Needed because the API is not consistent about it:
 * `formatStreams[].itag` is the string `"18"` while `lengthSeconds` is the number `214`.
 */
export function asIntLike(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? Math.trunc(value) : null;
  if (typeof value === 'string' && /^-?\d+$/.test(value.trim())) return Number(value.trim());
  return null;
}

export function asStringArray(value: unknown): readonly string[] {
  const array = asArray(value);
  if (!array) return [];
  const out: string[] = [];
  for (const entry of array) {
    const text = asString(entry);
    if (text !== null) out.push(text);
  }
  return out;
}

export function field(source: RawObject, key: string): unknown {
  return Object.prototype.hasOwnProperty.call(source, key) ? source[key] : undefined;
}

/**
 * Reads the first key that is present, for fields the API spells differently depending on which
 * endpoint answered. The concrete case (§2): the caption list endpoint returns `languageCode`
 * while the same field embedded in video details is `language_code`. A parser hard-coded to one
 * silently reads `undefined` from the other.
 */
export function firstPresent(source: RawObject, keys: readonly string[]): unknown {
  for (const key of keys) {
    const value = field(source, key);
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
}
